import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { readContract } from "./errors";
import { publicIdSchema } from "./public-id";

export const macrosSchema = z.object({
  items: z.array(
    z.object({
      public_id: publicIdSchema,
      name: z.string().nullable(),
      section: z.string().nullable(),
      details: z.string().nullable(),
    }),
  ),
});

export const macroWriteSchema = z.strictObject({
  name: z.string().trim().min(1).max(64),
  section: z.string().trim().max(32),
  details: z.string().trim().min(1).max(8000),
});
export type MacroWriteInput = z.input<typeof macroWriteSchema>;
export const macroWriteResultSchema = z.object({
  public_id: publicIdSchema,
});

const writeContract = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Macro not found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});

export const macroContract = {
  macros: readContract
    .errors({
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
    })
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/macros",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(macrosSchema),
  createMacro: writeContract
    .input(
      z.object({
        params: z.strictObject({}).default({}),
        query: z.strictObject({}).default({}),
        body: macroWriteSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/macros",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(macroWriteResultSchema),
  updateMacro: writeContract
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: macroWriteSchema,
      }),
    )
    .meta(
      openapi({
        method: "PUT",
        path: "/api/v2/macros/{public_id}",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(macroWriteResultSchema),
  deleteMacro: writeContract
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "DELETE",
        path: "/api/v2/macros/{public_id}",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(macroWriteResultSchema),
};
